import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

const chalk = '#f5f0e8';
const espresso = '#3d2b1f';
const terra = '#c2703e';
const ocher = '#d4a843';
const sand = '#e8dcc8';
const warm = '#8b7c6e';

export default function LandingV6() {
  return (
    <div className="doctif-page" style={{
      fontFamily: 'Figtree, sans-serif',
      background: chalk,
      color: espresso,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <Helmet><title>doctif — Solar Minimalist</title></Helmet>

      {/* Grain overlay */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 50,
        opacity: 0.035,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Sun orb */}
      <div aria-hidden style={{
        position: 'absolute', top: -200, right: '5%',
        width: 550, height: 550, borderRadius: '50%',
        background: `radial-gradient(circle, ${ocher}30 0%, transparent 70%)`,
      }} />

      {/* Header */}
      <header className="doctif-shell" style={{ padding: '30px 0 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'Fraunces, serif', fontSize: 30, letterSpacing: '0.06em' }}>doctif</span>
          <nav className="doctif-chip-row">
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <a key={n} className="doctif-chip" href={`/${n}`} style={{
                background: n === 6 ? espresso : sand,
                color: n === 6 ? chalk : espresso,
              }}>V{n}</a>
            ))}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="doctif-shell" style={{ padding: '56px 0 72px', position: 'relative' }}>
        <p style={{
          fontSize: 11, letterSpacing: '0.35em', textTransform: 'uppercase',
          color: terra, fontWeight: 600, marginBottom: 22,
        }}>Suite clinique solaire</p>

        <h1 style={{
          fontFamily: 'Fraunces, serif',
          fontSize: 'clamp(2.6rem, 7vw, 5.5rem)',
          fontWeight: 900, lineHeight: 1.0,
          maxWidth: 750, marginBottom: 26,
        }}>
          Le cabinet<br />
          <span style={{ color: terra }}>au ralenti</span><br />
          maîtrisé.
        </h1>

        <p style={{ fontSize: 18, lineHeight: 1.7, maxWidth: 500, color: warm, marginBottom: 34 }}>
          doctif coordonne rendez-vous, dossiers et facturation dans un flux unique —
          conçu pour poser le rythme sans perdre en efficacité.
        </p>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <a href="/accounts/login/" style={{
            background: terra, color: '#fff', borderRadius: 999,
            padding: '14px 26px', fontWeight: 700, textDecoration: 'none',
            letterSpacing: '0.04em', fontSize: 14,
            boxShadow: `0 8px 32px ${terra}44`,
            transition: 'transform 0.4s ease',
          }}>Essayer doctif</a>
          <a href="/booking" style={{
            border: `1px solid ${sand}`, color: espresso, borderRadius: 999,
            padding: '14px 26px', fontWeight: 600, textDecoration: 'none',
            letterSpacing: '0.04em', fontSize: 14,
          }}>Voir la démo</a>
        </div>

        {/* Sun disc */}
        <div aria-hidden style={{
          position: 'absolute', bottom: -30, left: '50%',
          transform: 'translateX(-50%)',
          width: 70, height: 70, borderRadius: '50%',
          background: `radial-gradient(circle, ${ocher} 30%, transparent 70%)`,
          opacity: 0.35,
        }} />
      </section>

      {/* Stats */}
      <div style={{ borderTop: `1px solid ${sand}`, borderBottom: `1px solid ${sand}` }}>
        <div className="doctif-shell" style={{
          padding: '26px 0', display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, textAlign: 'center',
        }}>
          {[
            { v: '240+', l: 'Cabinets actifs' },
            { v: '61k', l: 'Rendez-vous/mois' },
            { v: '34%', l: 'Temps gagné' },
          ].map(m => (
            <div key={m.l}>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 34, fontWeight: 900, color: terra }}>{m.v}</div>
              <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: warm, marginTop: 4 }}>{m.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <section className="doctif-shell" style={{ padding: '72px 0 56px' }}>
        <p style={{ fontSize: 11, letterSpacing: '0.35em', textTransform: 'uppercase', color: ocher, fontWeight: 600, marginBottom: 14 }}>Les quatre piliers</p>
        <h2 style={{
          fontFamily: 'Fraunces, serif',
          fontSize: 'clamp(1.6rem, 4vw, 2.8rem)', fontWeight: 700, lineHeight: 1.1, marginBottom: 48,
        }}>
          Simple par dessein,<br /><span style={{ color: terra }}>puissant par nature.</span>
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {[
            { n: '01', t: 'Agenda', d: 'Créneaux optimisés, rappels automatiques. Votre temps, respecté.' },
            { n: '02', t: 'Dossiers', d: 'Historiques, radios, notes — tout au même endroit, toujours.' },
            { n: '03', t: 'Facturation', d: 'Devis et factures sans friction. Relances automatisées.' },
            { n: '04', t: 'Multi-cabinet', d: 'Pilotez chaque site depuis un tableau unifié et serein.' },
          ].map(f => (
            <article key={f.n} style={{
              background: '#fff', border: `1px solid ${sand}`, borderRadius: 14,
              padding: '28px 24px',
              transition: 'box-shadow 0.5s ease, transform 0.4s ease',
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 16px 50px -12px ${terra}20`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 44, color: `${terra}15`, lineHeight: 1, marginBottom: 10 }}>{f.n}</div>
              <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{f.t}</h3>
              <p style={{ color: warm, fontSize: 14, lineHeight: 1.65 }}>{f.d}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="doctif-shell" style={{ paddingBottom: 72 }}>
        <div style={{
          borderRadius: 20, border: `1px solid ${sand}`,
          background: 'linear-gradient(165deg, #fff, #faf6ef)',
          padding: 24, boxShadow: `0 24px 50px -20px ${espresso}12`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <p style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: warm, fontWeight: 700 }}>Aperçu du jour</p>
              <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 24, marginTop: 4 }}>Mardi 25 Mars</h3>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['Agenda', 'Patients', 'Factures'].map((t, i) => (
                <span key={t} style={{
                  padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                  background: i === 0 ? terra : '#f3ede3',
                  color: i === 0 ? '#fff' : warm,
                }}>{t}</span>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { time: '08:30', patient: 'M. Benali', type: 'Contrôle', color: terra },
              { time: '09:15', patient: 'Mme. Idrissi', type: 'Détartrage', color: '#6b8e5b' },
              { time: '10:00', patient: 'M. Lahlou', type: 'Prothèse', color: ocher },
            ].map(a => (
              <div key={a.time} style={{
                background: '#faf6ef', borderRadius: 12, padding: '14px 12px',
                borderLeft: `3px solid ${a.color}`,
              }}>
                <div style={{ color: terra, fontWeight: 700, fontSize: 12, marginBottom: 5 }}>{a.time}</div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{a.patient}</div>
                <div style={{ color: warm, fontSize: 12, marginTop: 2 }}>{a.type}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section style={{ background: espresso, color: chalk, position: 'relative', overflow: 'hidden' }}>
        <div aria-hidden style={{
          position: 'absolute', width: 350, height: 350, borderRadius: '50%',
          background: `radial-gradient(circle, ${terra}22, transparent 70%)`,
          top: -80, right: -80,
        }} />
        <div className="doctif-shell" style={{ padding: '72px 0', position: 'relative' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p style={{ fontSize: 11, letterSpacing: '0.35em', textTransform: 'uppercase', color: ocher, marginBottom: 12 }}>Comment ça marche</p>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.6rem, 4vw, 2.6rem)', fontWeight: 700 }}>
              Du rendez-vous<br />à la <span style={{ color: terra }}>clôture.</span>
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            {[
              { s: '1', t: 'Réservation', d: 'Le patient choisit son créneau en ligne. Confirmation immédiate.', i: '📅' },
              { s: '2', t: 'Consultation', d: 'Notes, photos et actes codifiés directement au fauteuil.', i: '🦷' },
              { s: '3', t: 'Suivi', d: 'Facture envoyée, relance programmée, synthèse quotidienne.', i: '✉️' },
            ].map(w => (
              <div key={w.s} style={{
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 18, padding: '28px 20px', textAlign: 'center',
                transition: 'border-color 0.4s, transform 0.3s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = terra; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: 32, marginBottom: 12 }}>{w.i}</div>
                <div style={{ fontSize: 11, letterSpacing: '0.15em', color: ocher, marginBottom: 6, textTransform: 'uppercase' }}>Étape {w.s}</div>
                <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 20, fontWeight: 700, marginBottom: 10 }}>{w.t}</h3>
                <p style={{ color: '#b0a595', fontSize: 13, lineHeight: 1.6 }}>{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="doctif-shell" style={{ padding: '72px 0 56px' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <p style={{ fontSize: 11, letterSpacing: '0.35em', textTransform: 'uppercase', color: ocher, marginBottom: 12 }}>Témoignages</p>
          <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.4rem, 3.5vw, 2.4rem)', fontWeight: 700 }}>
            Praticiens <span style={{ color: terra }}>sereins.</span>
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {[
            { q: "Le rythme a changé. Moins de clics, plus de présence avec le patient.", n: 'Dr. Khalil', cl: 'Cabinet Al Madina — Casablanca', bg: `linear-gradient(135deg, ${terra}, #92400e)` },
            { q: "Multi-cabinet sans stress. Tout est unifié, clair, apaisant.", n: 'Dr. Brahmi', cl: 'Clinique Corbei — Oujda', bg: `linear-gradient(135deg, ${ocher}, #a67c20)` },
          ].map(t => (
            <div key={t.n} style={{
              background: '#fff', border: `1px solid ${sand}`, borderRadius: 18,
              padding: '26px 24px',
            }}>
              <div style={{ color: ocher, fontSize: 16, letterSpacing: 2, marginBottom: 12 }}>★★★★★</div>
              <blockquote style={{ fontFamily: 'Fraunces, serif', fontSize: 18, fontStyle: 'italic', lineHeight: 1.45, margin: '0 0 18px' }}>"{t.q}"</blockquote>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: '50%', background: t.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 800, color: '#fff',
                }}>{t.n.split(' ').map(w => w[0]).join('')}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{t.n}</div>
                  <div style={{ color: warm, fontSize: 12 }}>{t.cl}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <div style={{ borderTop: `1px solid ${sand}`, borderBottom: `1px solid ${sand}`, background: 'linear-gradient(165deg, #f5f0e8, #faf3e3, #f5f0e8)' }}>
        <div className="doctif-shell" style={{ padding: '34px 0', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, textAlign: 'center' }}>
          {[
            { i: '🔒', t: 'Chiffrement AES-256', d: 'Transit et repos' },
            { i: '🇲🇦', t: 'Hébergé au Maroc', d: 'Conformité locale' },
            { i: '⚡', t: 'Temps réel', d: '< 1 seconde' },
            { i: '🎧', t: 'Support dédié', d: '< 2h ouvrées' },
          ].map(t => (
            <div key={t.t}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>{t.i}</div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{t.t}</div>
              <div style={{ color: warm, fontSize: 12 }}>{t.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <section className="doctif-shell" style={{ padding: '72px 0', textAlign: 'center' }}>
        <h2 style={{
          fontFamily: 'Fraunces, serif',
          fontSize: 'clamp(1.8rem, 5vw, 3.2rem)',
          fontWeight: 700, marginBottom: 14, lineHeight: 1.05,
        }}>
          Prêt à poser<br /><span style={{ color: terra }}>le rythme ?</span>
        </h2>
        <p style={{ color: warm, maxWidth: 420, margin: '0 auto 28px', fontSize: 15, lineHeight: 1.6 }}>
          Rejoignez les praticiens qui ont choisi la sérénité. Sans engagement.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
          <a href="/accounts/login/" style={{
            background: terra, color: '#fff', padding: '15px 30px', borderRadius: 999,
            fontWeight: 700, fontSize: 15, textDecoration: 'none',
            boxShadow: `0 10px 40px ${terra}33`,
          }}>Démarrer avec doctif →</a>
          <a href="/booking" style={{
            background: '#fff', color: espresso, padding: '15px 26px', borderRadius: 999,
            fontWeight: 600, fontSize: 15, textDecoration: 'none',
            border: `1px solid ${sand}`,
          }}>Voir la démo</a>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${sand}` }}>
        <div className="doctif-shell" style={{ padding: '24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: 'Fraunces, serif', fontSize: 18, color: warm }}>doctif</span>
            <span style={{ color: sand }}>|</span>
            <span style={{ color: warm, fontSize: 11, letterSpacing: '0.1em' }}>Direction Solar — Version 06</span>
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            {['Confidentialité', 'CGU', 'Contact'].map(l => (
              <a key={l} href="#" style={{ color: warm, fontSize: 11, textDecoration: 'none', fontWeight: 600 }}
                onMouseEnter={e => (e.currentTarget.style.color = terra)}
                onMouseLeave={e => (e.currentTarget.style.color = warm)}
              >{l}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
